import type { ERelationBehaviour } from 'api/meta/client-metaapi';
import type { CalculatedSemNode } from 'types/data/CalculatedSemNode';
import type { IDictionary, SemNode } from 'types/data/semNode';
import { hasValue } from 'utils/values';

import type { ColumnSpecification, TableSpecification } from '../selectedProject/selectedProjectHooks';

export interface GridSpecification {
  resultSetId: string;
  width: number;
  columnWidthsByIndex: { [index: number]: number }; // column width by index
  columnIndexByKey: { [edgeKey: string]: number };
  nodeTraversalIdByColumnKey: { [columnKey: string]: string };
  columnWidthsByKey: { [key: string]: number };
  /** the value is the list of the keys of the subsequent edges */
  edgeKeyMap: { [parentEdgeKey: string]: string[] };
  columnKeyMap: { [edgeKey: string]: string[] } | null;
  propertyKeyMap: { [edgeKey: string]: string[] }; // properties under and edge key
  nodeMatrixColumnIndexMap: { [index: number]: number }; // column datacolum reference (e.g. column 5 must use column 4 in the nodematrix)
  columnAppearanceMap: { [index: number]: ColumnAppearance }; // column appearance by column index
  columnSpecificationByIndexMap: { [index: number]: ColumnSpecification }; // column specification by column index
  /** Note: this is not always correct. Prevent using this for new features until verified with BE. */
  isOverview: boolean;
}

export interface ColumnAppearance {
  isRoot: boolean;
  parentBehaviour?: ERelationBehaviour | null;
  isLastSyntacticCell: boolean;
  isFirstSyntacticCell: boolean;
  isSemanticEdit: boolean;
  picklistId?: string | null;
  showDisconnect: boolean;
  disabled: boolean;
}

export interface NodeProperties {
  nodeId: string;
  properties: Record<string, PropertyValue>;
}

export type PropertyValue = SemNode['Properties'][keyof SemNode['Properties']];

export function getPropertyValue<T = string>(
  key: string,
  properties: Record<string, PropertyValue>,
  transform: (value: PropertyValue) => T = (value) => (hasValue(value) ? (value.toString() as T) : ('' as T))
): T {
  const value = properties[key];
  return transform(value);
}

export interface ScrollPosition {
  resultSetId: string;
  gridPositionStart?: number;
  gridPositionEnd?: number;
}

export interface QueryInitialization {
  resultSetId: string;
  specification: TableSpecification;
  keyMap: IDictionary<number>;
  rootRowMap: Record<number, number>;
  nodeMatrix: CalculatedSemNode[][];
  gridSpecification: GridSpecification;
}

export type DataFetchHandle = {
  cancel: () => void;
  projectId: string;
  queryId: string;
  dataNodeId?: string;
};

export const getNodeProperties = (nodes: SemNode[]): NodeProperties[] => {
  const nodeProperties: NodeProperties[] = [];
  nodes.forEach((node) => getFlattenedNodeProperties(node, nodeProperties));
  return nodeProperties;
};

const getFlattenedNodeProperties = (node: SemNode, nodeProperties: NodeProperties[]) => {
  const propertyValues = { ...node.Properties };
  const thisNodeProperties: NodeProperties = { nodeId: node.Id, properties: propertyValues };
  nodeProperties.push(thisNodeProperties);

  for (const edgeKey of Object.keys(node.Edges)) {
    node.Edges[edgeKey].forEach((edge) => {
      getFlattenedNodeProperties(edge.Target, nodeProperties);
    });
  }
};
